import React from 'react';

export const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];

interface PaginationProps {
  /** 1-based current page. */
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  /** Disables the controls while a page is loading. */
  disabled?: boolean;
  /** Distinguishes several paginators on one page for screen readers. */
  label?: string;
}

type PageItem = number | 'gap-left' | 'gap-right';

/**
 * Page numbers to show: always the first and last page, the current page and
 * one neighbour either side, with a gap marker wherever pages are skipped.
 * e.g. page 6 of 12 -> 1 … 5 6 7 … 12
 */
const pageItems = (page: number, totalPages: number): PageItem[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const start = Math.max(2, Math.min(page - 1, totalPages - 4));
  const end = Math.min(totalPages - 1, Math.max(page + 1, 5));
  const items: PageItem[] = [1];
  if (start > 2) items.push('gap-left');
  for (let p = start; p <= end; p += 1) items.push(p);
  if (end < totalPages - 1) items.push('gap-right');
  items.push(totalPages);
  return items;
};

const navButton =
  'px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed transition-all';

/**
 * Full pagination bar: page size, "x–y of n", first/previous/next/last and
 * numbered pages. Stateless - the parent owns page and page size.
 */
const Pagination: React.FC<PaginationProps> = ({
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  disabled = false,
  label = 'Pagination',
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const first = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, totalItems);
  const goTo = (target: number) => {
    const clamped = Math.min(Math.max(target, 1), totalPages);
    if (clamped !== page) onPageChange(clamped);
  };
  const selectId = `page-size-${label.replace(/\W+/g, '-').toLowerCase()}`;

  return (
    <nav
      aria-label={label}
      className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 bg-gray-50 border-t border-gray-200"
    >
      <div className="flex items-center gap-2 text-xs text-gray-600">
        <label htmlFor={selectId}>Rows per page:</label>
        <select
          id={selectId}
          value={pageSize}
          disabled={disabled}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all"
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
        <span aria-live="polite">
          {first}–{last} of {totalItems}
        </span>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center gap-1">
          <button type="button" onClick={() => goTo(1)} disabled={disabled || page === 1} aria-label="First page" className={navButton}>
            <span aria-hidden="true">«</span>
          </button>
          <button type="button" onClick={() => goTo(page - 1)} disabled={disabled || page === 1} aria-label="Previous page" className={navButton}>
            <span aria-hidden="true">‹</span>
          </button>

          {pageItems(page, totalPages).map((item) =>
            typeof item === 'number' ? (
              <button
                key={item}
                type="button"
                onClick={() => goTo(item)}
                disabled={disabled}
                aria-label={`Page ${item}`}
                aria-current={item === page ? 'page' : undefined}
                className={`min-w-[2rem] px-2.5 py-1 text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-all ${
                  item === page
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {item}
              </button>
            ) : (
              <span key={item} className="px-1 text-xs text-gray-400" aria-hidden="true">…</span>
            )
          )}

          <button type="button" onClick={() => goTo(page + 1)} disabled={disabled || page === totalPages} aria-label="Next page" className={navButton}>
            <span aria-hidden="true">›</span>
          </button>
          <button type="button" onClick={() => goTo(totalPages)} disabled={disabled || page === totalPages} aria-label="Last page" className={navButton}>
            <span aria-hidden="true">»</span>
          </button>
        </div>
      )}
    </nav>
  );
};

export default Pagination;
